/*
  Swish classic wishlist page — <swish-wishlist-page>.

  Store-agnostic: all theme markup and class names come from the section
  (sections/wishlist-main.liquid), so this file can be copied between stores
  unchanged. Follows the Swish SDK "Custom wishlist page" example
  (https://swish.app/docs/references/sdk/examples#custom-wishlist-page):
    - swish.state.listContext(window.location) picks the list from the URL
      (all items, ?list-id=, or a shared ?public-list-id= -> read-only)
    - swish.state.items(context) is the live, paginated collection; it re-renders
      on every save/unsave/sign-in, so we never keep our own copy
    - swish.state.product(ids) resolves each card (cached + batched by the SDK)
    - actions go through intents: unsave:item, create:cart-line, initiate:sign-in

  Each card shows option chips for every option that varies. Availability is
  decoded from the product's encodedVariantAvailability; picking a full
  combination resolves the variant via
  swish.storefront.loadResolvedProduct({ selectedOptions }) and stores it on the
  saved item (swish.api.items.updateById — variantId must be a NUMBER).

  ---------------------------------------------------------------------------
  Section contract
  ---------------------------------------------------------------------------
  <swish-wishlist-page> attributes
    data-label-add              "Add to bag" button label
    data-label-select           label while a size-like option is unpicked
    data-label-select-option    optional, other options; "[option]" = name
                                (default "Select [option]")
    data-label-sold-out         sold-out button label
    data-label-in-cart          optional; label when the card's variant is already
                                in the cart (kept in sync with the Shopify cart via
                                swish cart events, so mini-cart edits update it).
                                Clicking it fires a bubbling, cancelable
                                `swish-wishlist:view-cart` event ({ variantId });
                                if nothing cancels it, the page goes to /cart.
    data-heading-shared         heading for a shared (read-only) list
    data-money-format           {{ shop.money_format | escape }}
    data-money-trim-zeros       optional; drop zero cents ("$500.00" -> "$500"),
                                like Liquid's money_without_trailing_zeros
    data-hidden-class           theme class that hides an element (e.g. "hidden").
                                Use it when the elements below carry display
                                utilities (.flex/.grid/.btn) that beat [hidden].
                                Omit to toggle the `hidden` attribute instead.
    data-sale-class             optional class added to the price when on sale
    data-chip-active-class      class for the selected chip (e.g. "active")
    data-chip-unavailable-class class(es) for a sold-out chip
    data-option-display         optional; "chips" (default) or "select". "select"
                                renders each option as a <select> built from
                                template[data-wishlist-select] (falls back to
                                chips when that template is missing)
    data-select-placeholder     optional, select mode; placeholder option text,
                                "[option]" = name (default "Select [option]")
    data-select-unavailable-suffix
                                optional, select mode; appended to a sold-out
                                value's text (e.g. " - Unavailable")

  Elements (inside the root)
    [data-wishlist-heading]        h1; swapped for data-heading-shared
    [data-wishlist-grid]           card container
    [data-wishlist-empty]          empty state
    [data-wishlist-load-more]      Load more button
    [data-wishlist-load-more-wrap] optional wrapper hidden with the button
    [data-wishlist-login]          any number of "Login & Save" buttons
    [data-wishlist-login-top]      optional; shown only when there are items

  Templates
    template[data-wishlist-card]        one card (required)
    template[data-wishlist-in-cart]     optional rich content for the in-cart button
                                        (e.g. a check icon + label); falls back to
                                        data-label-in-cart as plain text
    template[data-wishlist-option-row]  optional row wrapper for chips. May contain
                                        [data-option-name] (filled with the option
                                        name, e.g. "Size") and [data-option-values]
                                        (where the chips go; default: the row)
    template[data-wishlist-chip]        optional chip <button>
    template[data-wishlist-select]      select mode: one option field. Its first
                                        element is a <select> or contains one
                                        (e.g. a theme dropdown component that
                                        wraps a native select). The <select> is
                                        filled before the field is connected, and
                                        its `change` event picks the value, so
                                        custom dropdowns that set select.value and
                                        dispatch `change` work as-is. Sold-out
                                        values get data-unavailable="true" and are
                                        disabled unless selected. May contain
                                        [data-option-name] (filled with the option
                                        name).

  Card hooks (inside the card template)
    [data-card-link] (any number), [data-card-image], [data-card-title],
    [data-card-price], [data-card-compare], [data-card-options], [data-card-atc],
    [data-card-remove] (any number), [data-card-owner-only] (removed on shared lists)
*/
(function () {
  if (customElements.get('swish-wishlist-page')) return;

  function whenSwish() {
    return new Promise(function (resolve) {
      if (window.swish && window.swish.state) return resolve(window.swish);
      document.addEventListener('swish-ready', function () { resolve(window.swish); }, { once: true });
    });
  }

  function numericId(id) {
    return id == null ? null : String(id).split('/').pop();
  }

  function classes(value) {
    return (value || '').split(/\s+/).filter(Boolean);
  }

  // Shopify money format ({{amount}}, {{amount_no_decimals}}, ...). Any HTML in
  // the format (e.g. <span class=money>) is stripped; we render text only.
  // trimZeros drops zero cents, like Liquid's money_without_trailing_zeros.
  function formatMoney(money, format, trimZeros) {
    if (!money || money.amount == null) return '';
    var amount = parseFloat(money.amount);
    var template = (format || '${{amount}}').replace(/<[^>]*>/g, '');
    function withDelimiters(value, decimals, thousands, decimal) {
      var parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    if (trimZeros && Math.round(amount * 100) % 100 === 0) {
      template = template.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, function (_, comma) {
        return comma ? '{{amount_no_decimals_with_comma_separator}}' : '{{amount_no_decimals}}';
      });
    }
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(amount, 2, "'", '.');
        case 'amount_with_space_separator': return withDelimiters(amount, 2, ' ', ',');
        default: return withDelimiters(amount, 2, ',', '.');
      }
    });
  }

  // Fill any [data-option-name] inside a row/field with the option's name.
  function fillOptionName(root, option) {
    root.querySelectorAll('[data-option-name]').forEach(function (el) {
      el.textContent = option.name;
    });
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function productUrl(handle, variantId) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'products/' + handle + (variantId ? '?variant=' + variantId : '');
  }

  /*
    Decode Shopify's encoded variant availability/existence string
    ("v1_0:0-2,1:1," etc.) into option-value index tuples, e.g. [[0,0],[0,1]].
    Port of Shopify's reference decoder (Storefront API, encodedVariantAvailability).
    Returns null when the string is missing or in an unknown version.
  */
  function decodeOptionValues(encoded) {
    if (!encoded || encoded.indexOf('v1_') !== 0) return null;
    var input = encoded.slice(3);
    var tokenizer = /[ :,-]/g;
    var index = 0;
    var token;
    var tuples = [];
    var current = [];
    var depth = 0;
    var rangeStart = null;

    while ((token = tokenizer.exec(input))) {
      var operation = token[0];
      var value = parseInt(input.slice(index, token.index), 10) || 0;
      if (rangeStart !== null) {
        for (; rangeStart < value; rangeStart++) {
          current[depth] = rangeStart;
          tuples.push(current.slice());
        }
        rangeStart = null;
      }
      current[depth] = value;
      if (operation === '-') {
        rangeStart = value;
      } else if (operation === ':') {
        depth++;
      } else {
        if (operation === ' ' || (operation === ',' && input[token.index - 1] !== ',')) {
          tuples.push(current.slice());
        }
        if (operation === ',') {
          current.pop();
          depth--;
        }
      }
      index = tokenizer.lastIndex;
    }

    var tail = input.match(/\d+$/);
    if (tail) {
      var last = parseInt(tail[0], 10);
      if (rangeStart !== null) {
        for (; rangeStart <= last; rangeStart++) {
          current[depth] = rangeStart;
          tuples.push(current.slice());
        }
      } else {
        current[depth] = last;
        tuples.push(current.slice());
      }
    }
    return tuples;
  }

  function optionValues(option) {
    return (option.optionValues || option.values || []).map(function (v) {
      return typeof v === 'string' ? v : v.name;
    });
  }

  class SwishWishlistPage extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this.cards = new Map();
      this.disposers = [];
      whenSwish().then(this.start.bind(this));
    }

    disconnectedCallback() {
      this.disposers.forEach(function (dispose) { dispose(); });
      this.cards.forEach(function (card) { card.dispose(); });
      this.cards.clear();
    }

    // Hide/show via the theme's hide class when configured (display utilities
    // like .flex/.btn override the [hidden] attribute), else the attribute.
    setHidden(el, hide) {
      if (!el) return;
      var hiddenClass = this.dataset.hiddenClass;
      if (hiddenClass) el.classList.toggle(hiddenClass, hide);
      else el.hidden = hide;
    }

    start(swish) {
      var self = this;
      this.swish = swish;
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.heading = this.querySelector('[data-wishlist-heading]');
      this.loadMore = this.querySelector('[data-wishlist-load-more]');
      this.loadMoreWrap = this.querySelector('[data-wishlist-load-more-wrap]') || this.loadMore;
      this.loginTop = this.querySelector('[data-wishlist-login-top]');
      this.template = this.querySelector('template[data-wishlist-card]');
      this.rowTemplate = this.querySelector('template[data-wishlist-option-row]');
      this.chipTemplate = this.querySelector('template[data-wishlist-chip]');
      this.selectTemplate = this.querySelector('template[data-wishlist-select]');
      this.inCartTemplate = this.querySelector('template[data-wishlist-in-cart]');
      this.defaultHeading = this.heading ? this.heading.textContent : '';

      this.querySelectorAll('[data-wishlist-login]').forEach(function (button) {
        button.addEventListener('click', function () {
          swish.intents.invoke('initiate:sign-in', { returnTo: window.location.href });
        });
      });

      var context = swish.state.listContext(window.location);
      var items = swish.state.items(context);
      this.disposers.push(function () { items.dispose(); context.dispose(); });

      this.disposers.push(swish.state.effect(function () {
        self.readOnly = context.value.listType === 'shared';
        if (self.heading && self.dataset.headingShared) {
          self.heading.textContent = self.readOnly ? self.dataset.headingShared : self.defaultHeading;
        }
      }));

      // Read the signal inside the effect, render outside it so the per-card
      // effects created during render aren't tracked by this one.
      this.disposers.push(swish.state.effect(function () {
        var value = items.value;
        if (value.loading) return;
        queueMicrotask(function () { self.render(value); });
      }));

      if (this.loadMore) this.loadMore.addEventListener('click', function () { items.loadMore(); });

      // "In cart" state: variant IDs currently in the Shopify cart. Refreshed on
      // every cart call the SDK sees (ours, the theme's ATC, mini-cart edits).
      this.cartVariants = new Set();
      if (this.dataset.labelInCart) {
        var refreshCart = function () {
          swish.ajax.fetchCart().then(function (cart) {
            self.cartVariants = new Set(((cart && cart.items) || []).map(function (line) { return String(line.variant_id); }));
            self.cards.forEach(function (card) { card.refresh(); });
          }).catch(function () {});
        };
        refreshCart();
        this.disposers.push(swish.events.subscribe(['cart-add', 'cart-update', 'cart-change', 'cart-clear'], refreshCart));
      }
    }

    isInCart(variantId) {
      return !!this.dataset.labelInCart && this.cartVariants.has(String(numericId(variantId)));
    }

    render(value) {
      var self = this;
      var saved = value.items || [];
      var ids = new Set(saved.map(function (item) { return item.id; }));

      this.cards.forEach(function (card, id) {
        if (!ids.has(id)) {
          card.dispose();
          self.cards.delete(id);
        }
      });

      var elements = saved.map(function (item) {
        var card = self.cards.get(item.id);
        if (card && card.variantId !== item.variantId) {
          card.dispose();
          card = null;
        }
        if (!card) {
          card = self.createCard(item);
          self.cards.set(item.id, card);
        }
        return card.el;
      });

      this.grid.replaceChildren.apply(this.grid, elements);
      this.setHidden(this.grid, saved.length === 0);
      this.setHidden(this.empty, saved.length > 0);
      // The top Login & Save shows only alongside items; the empty state can
      // carry its own [data-wishlist-login] button.
      this.setHidden(this.loginTop, saved.length === 0 || this.readOnly);

      this.setHidden(this.loadMoreWrap, !value.hasMore);
      if (this.loadMore) this.loadMore.disabled = !!value.loadingMore;
    }

    createChipRow(option) {
      var row = this.rowTemplate
        ? this.rowTemplate.content.firstElementChild.cloneNode(true)
        : document.createElement('div');
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', option.name);
      fillOptionName(row, option);
      return row;
    }

    createChip(label, available, selected) {
      var chip = this.chipTemplate
        ? this.chipTemplate.content.firstElementChild.cloneNode(true)
        : document.createElement('button');
      chip.type = 'button';
      chip.textContent = label;
      if (!available) classes(this.dataset.chipUnavailableClass).forEach(function (c) { chip.classList.add(c); });
      if (selected) classes(this.dataset.chipActiveClass).forEach(function (c) { chip.classList.add(c); });
      chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (!available && !selected) chip.disabled = true;
      return chip;
    }

    // Select mode: one field per option, cloned from template[data-wishlist-select].
    // Filled while still disconnected, so a wrapping custom element (e.g. the
    // theme's dropdown) builds its UI from the finished <select> on connect.
    createSelectField(option, values, selectedIndex, isAvailable) {
      var field = this.selectTemplate.content.firstElementChild.cloneNode(true);
      var select = field.matches('select') ? field : field.querySelector('select');
      var placeholder = (this.dataset.selectPlaceholder || 'Select [option]').replace('[option]', option.name);
      var suffix = this.dataset.selectUnavailableSuffix || '';
      select.name = 'swish-wishlist-option';
      select.setAttribute('aria-label', option.name);
      select.setAttribute('placeholder', placeholder);
      select.dataset.placeholder = placeholder;
      select.replaceChildren();
      fillOptionName(field, option);

      var blank = new Option(placeholder, '', selectedIndex == null, selectedIndex == null);
      blank.disabled = true;
      blank.hidden = true;
      select.add(blank);
      values.forEach(function (value, i) {
        var available = isAvailable(i);
        var selected = i === selectedIndex;
        var opt = new Option(available ? value : value + suffix, value, selected, selected);
        opt.dataset.unavailable = available ? 'false' : 'true';
        if (!available && !selected) opt.disabled = true;
        select.add(opt);
      });
      return { el: field, select: select };
    }

    createCard(item) {
      var self = this;
      var swish = this.swish;
      var readOnly = this.readOnly;
      var moneyFormat = this.dataset.moneyFormat;
      var trimZeros = 'moneyTrimZeros' in this.dataset;
      var saleClasses = classes(this.dataset.saleClass);
      var el = this.template.content.firstElementChild.cloneNode(true);
      var links = el.querySelectorAll('[data-card-link]');
      var image = el.querySelector('[data-card-image]');
      var title = el.querySelector('[data-card-title]');
      var price = el.querySelector('[data-card-price]');
      var compare = el.querySelector('[data-card-compare]');
      var optionsEl = el.querySelector('[data-card-options]');
      var atc = el.querySelector('[data-card-atc]');

      if (readOnly) {
        el.querySelectorAll('[data-card-owner-only]').forEach(function (node) { node.remove(); });
      } else {
        el.querySelectorAll('[data-card-remove]').forEach(function (button) {
          button.addEventListener('click', function () {
            swish.intents.invoke('unsave:item', { itemId: item.id });
          });
        });
      }

      // product: resolved product; variant: the current full selection (or null);
      // selection: option-value index per option (null = not chosen yet).
      var state = { product: null, variant: null, selection: [], available: null, requestId: 0 };

      function isAvailable(optionIndex, valueIndex) {
        if (!state.available) return true;
        return state.available.some(function (tuple) {
          if (tuple[optionIndex] !== valueIndex) return false;
          return state.selection.every(function (selected, i) {
            return i === optionIndex || selected == null || tuple[i] === selected;
          });
        });
      }

      function renderPrice() {
        if (!price) return;
        var product = state.product;
        var source = state.variant || product.selectedOrFirstAvailableVariant;
        var amount = (source && source.price) || (product.priceRange && product.priceRange.minVariantPrice);
        var compareAt = source && source.compareAtPrice;
        var onSale = !!(compareAt && parseFloat(compareAt.amount) > parseFloat(amount && amount.amount));
        price.textContent = formatMoney(amount, moneyFormat, trimZeros);
        saleClasses.forEach(function (c) { price.classList.toggle(c, onSale); });
        if (compare) {
          compare.textContent = onSale ? formatMoney(compareAt, moneyFormat, trimZeros) : '';
          compare.hidden = !onSale;
        }
      }

      function renderImage() {
        if (!image) return;
        var img = (state.variant && state.variant.image) || state.product.featuredImage;
        image.hidden = !(img && img.url);
        if (img && img.url) {
          image.src = sizedImage(img.url, 600);
          image.srcset = [400, 600, 800].map(function (w) { return sizedImage(img.url, w) + ' ' + w + 'w'; }).join(', ');
          image.alt = img.altText || state.product.title;
        }
      }

      function renderButton() {
        if (!atc) return;
        var product = state.product;
        var inCart = !!state.variant && self.isInCart(state.variant.id);
        atc.toggleAttribute('data-in-cart', inCart);
        if (inCart) {
          if (self.inCartTemplate) atc.replaceChildren(self.inCartTemplate.content.cloneNode(true));
          else atc.textContent = self.dataset.labelInCart;
          atc.disabled = false;
        } else if (state.variant) {
          atc.textContent = state.variant.availableForSale ? self.dataset.labelAdd : self.dataset.labelSoldOut;
          atc.disabled = !state.variant.availableForSale;
        } else if (!product.availableForSale) {
          atc.textContent = self.dataset.labelSoldOut;
          atc.disabled = true;
        } else {
          // Theme's "Select size" for size-like options, "Select <Option>" otherwise.
          var missing = (product.options || []).find(function (_option, i) { return state.selection[i] == null; });
          var other = self.dataset.labelSelectOption || 'Select [option]';
          atc.textContent = !missing || /size/i.test(missing.name)
            ? self.dataset.labelSelect
            : other.replace('[option]', missing.name);
          atc.disabled = true;
        }
      }

      function renderOptions() {
        if (!optionsEl) return;
        var product = state.product;
        var useSelect = self.dataset.optionDisplay === 'select' && !!self.selectTemplate;
        var rows = (product.options || []).map(function (option, optionIndex) {
          var values = optionValues(option);
          if (values.length < 2) return null;
          if (useSelect) {
            var field = self.createSelectField(option, values, state.selection[optionIndex], function (valueIndex) {
              return isAvailable(optionIndex, valueIndex);
            });
            field.select.addEventListener('change', function () {
              var valueIndex = values.indexOf(field.select.value);
              // Some dropdowns dispatch `change` more than once per pick.
              if (valueIndex === -1 || state.selection[optionIndex] === valueIndex) return;
              select(optionIndex, valueIndex);
            });
            return field.el;
          }
          var row = self.createChipRow(option);
          var chips = row.querySelector('[data-option-values]') || row;
          values.forEach(function (value, valueIndex) {
            var chip = self.createChip(value, isAvailable(optionIndex, valueIndex), state.selection[optionIndex] === valueIndex);
            chip.addEventListener('click', function () { select(optionIndex, valueIndex); });
            chips.appendChild(chip);
          });
          return row;
        }).filter(Boolean);
        optionsEl.replaceChildren.apply(optionsEl, rows);
        optionsEl.hidden = rows.length === 0;
      }

      function renderAll() {
        renderPrice();
        renderImage();
        renderOptions();
        renderButton();
      }

      function select(optionIndex, valueIndex) {
        var product = state.product;
        state.selection[optionIndex] = valueIndex;
        var complete = state.selection.every(function (v) { return v != null; });
        if (!complete) {
          state.variant = null;
          renderAll();
          return;
        }
        var selectedOptions = (product.options || []).map(function (option, i) {
          return { name: option.name, value: optionValues(option)[state.selection[i]] };
        });
        var requestId = ++state.requestId;
        if (atc) atc.disabled = true;
        swish.storefront
          .loadResolvedProduct({ productId: numericId(product.id), selectedOptions: selectedOptions })
          .then(function (result) {
            if (requestId !== state.requestId) return;
            state.variant = result && result.variant;
            renderAll();
            // Remember the choice on the saved item (silent API update — no
            // item-update event, so the card keeps its local state). The API
            // rejects a string variantId; it must be a number.
            if (state.variant && !readOnly) {
              swish.api.items
                .updateById(item.id, { variantId: Number(numericId(state.variant.id)) })
                .catch(function () {});
            }
          })
          .catch(function () {
            if (requestId === state.requestId) renderAll();
          });
      }

      if (atc) {
        atc.addEventListener('click', function () {
          if (!state.variant) return;
          var variantId = numericId(state.variant.id);
          if (self.isInCart(variantId)) {
            var proceed = atc.dispatchEvent(new CustomEvent('swish-wishlist:view-cart', {
              bubbles: true, cancelable: true, detail: { variantId: variantId },
            }));
            if (proceed) {
              var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              window.location.href = root + 'cart';
            }
            return;
          }
          atc.disabled = true;
          // invoke() resolves to { complete }; mark the variant in cart as soon as
          // the add succeeds, then the cart events confirm it.
          Promise.resolve(swish.intents.invoke('create:cart-line', { variantId: variantId }))
            .then(function (invocation) { return invocation && invocation.complete; })
            .then(function (response) {
              if (response && response.code === 'ok') self.cartVariants.add(String(variantId));
            })
            .catch(function () {})
            .finally(renderButton);
        });
      }

      var resolved = swish.state.product({ productId: item.productId, variantId: item.variantId });
      var stopEffect = swish.state.effect(function () {
        var value = resolved.value;
        var product = value.product;
        if (!product) return;
        var variant = item.variantId ? value.variant : null;
        var options = product.options || [];

        state.product = product;
        state.variant = variant;
        state.available = decodeOptionValues(product.encodedVariantAvailability);
        // Start from the saved variant; single-value options are implicitly chosen.
        state.selection = options.map(function (option) {
          var values = optionValues(option);
          if (values.length === 1) return 0;
          var picked = variant && (variant.selectedOptions || []).find(function (o) { return o.name === option.name; });
          var index = picked ? values.indexOf(picked.value) : -1;
          return index === -1 ? null : index;
        });

        var url = productUrl(product.handle, item.variantId && numericId(item.variantId));
        links.forEach(function (link) {
          link.href = url;
          link.setAttribute('aria-label', product.title);
        });
        if (title) title.textContent = product.title;
        renderAll();
      });

      return {
        el: el,
        variantId: item.variantId,
        refresh: function () {
          if (state.product) renderButton();
        },
        dispose: function () {
          stopEffect();
          resolved.dispose();
        },
      };
    }
  }

  customElements.define('swish-wishlist-page', SwishWishlistPage);
})();
